import { useState, useEffect } from 'react'
import { Search, Menu, Keyboard } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Breadcrumbs } from '@/components/ui/breadcrumbs'
import { NotificationsButton, NotificationsPanel } from '@/components/NotificationsPanel'
import { HelpButton } from '@/components/HelpPanel'
import { SearchDialog } from '@/components/SearchDialog'
import { ThemeToggle } from '@/components/ui/theme-toggle'

interface HeaderProps {
  title?: string
  onMenuClick?: () => void
  onShowShortcuts?: () => void
}

export function Header({ title, onMenuClick, onShowShortcuts }: HeaderProps) {
  const [isNotificationsPanelOpen, setIsNotificationsPanelOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  return (
    <>
      <header className="flex h-20 items-center justify-between border-b border-border/50 bg-card px-6">
        <div className="flex items-center gap-4">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden rounded-xl"
            onClick={onMenuClick}
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </Button>
          <div className="flex flex-col gap-1">
            {title && <h1 className="text-xl font-serif font-medium">{title}</h1>}
            <Breadcrumbs />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Search Button */}
          <Button
            variant="outline"
            className="relative w-48 sm:w-64 justify-start text-sm text-foreground/60 rounded-xl border-border/50 hover:bg-background hover:border-border"
            onClick={() => setSearchOpen(true)}
            aria-label="Open search (Cmd+K)"
          >
            <Search className="mr-2 h-4 w-4" />
            <span className="flex-1 text-left">Search...</span>
            <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded-lg border border-border/50 bg-background px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
              <span className="text-xs">⌘</span>K
            </kbd>
          </Button>

          {/* Actions */}
          <Button
            variant="ghost"
            size="sm"
            className="hidden sm:flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            onClick={onShowShortcuts}
            aria-label="View keyboard shortcuts"
          >
            <Keyboard className="h-4 w-4" />
            <span className="text-xs">Press ? for shortcuts</span>
          </Button>
          <ThemeToggle />
          <HelpButton />
          <NotificationsButton onClick={() => setIsNotificationsPanelOpen(true)} />
        </div>
      </header>

      {/* Panels and Dialogs */}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
      <NotificationsPanel
        isOpen={isNotificationsPanelOpen}
        onClose={() => setIsNotificationsPanelOpen(false)}
      />
    </>
  )
}
